import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customerService } from '../services/customerService';
import { billingService } from '../services/billingService';
import { paymentService } from '../services/paymentService';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Chip,
  Container,
  CircularProgress,
  IconButton,
  Alert,
  Stack,
  Tabs,
  Tab,
  Divider,
  useTheme,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  WaterDrop as WaterIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  Percent as PercentIcon,
  CalendarMonth as CalendarIcon,
  Opacity as OpacityIcon,
  AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon,
  LocalOffer as TagIcon,
  ReceiptLong as ReceiptIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import MobileBottomBar from './common/MobileBottomBar';

const CheckWaterBills = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [customerId, setCustomerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [unpaidBills, setUnpaidBills] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [surchargeSettings, setSurchargeSettings] = useState(null);
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const [onlinePaymentDialogOpen, setOnlinePaymentDialogOpen] = useState(false);
  const [onlinePaymentSession, setOnlinePaymentSession] = useState(null);
  const [onlinePaymentLoading, setOnlinePaymentLoading] = useState(false);
  const [onlinePaymentError, setOnlinePaymentError] = useState('');

  // Fetch surcharge settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      const result = await paymentService.getSurchargeSettings();
      if (result.success && result.data) {
        setSurchargeSettings(result.data);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('paymongoLastSession');
    if (!stored) return;

    let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch {
      localStorage.removeItem('paymongoLastSession');
      return;
    }

    const { checkoutSessionId, customerId: storedCustomerId, billIds } = parsed || {};

    if (
      !checkoutSessionId ||
      !storedCustomerId ||
      !Array.isArray(billIds) ||
      billIds.length === 0
    ) {
      localStorage.removeItem('paymongoLastSession');
      return;
    }

    const confirmPayment = async () => {
      const result = await paymentService.confirmOnlinePayment({
        checkoutSessionId,
        customerId: storedCustomerId,
        billIds
      });

      localStorage.removeItem('paymongoLastSession');

      if (
        result &&
        result.success &&
        result.data &&
        result.data.success &&
        result.data.status === 'paid'
      ) {
        setCustomerId(storedCustomerId);
        setActiveTab(1);
        handleSearch(null, storedCustomerId);
      }
    };

    confirmPayment();
  }, []);

  // Helper function to round to 2 decimals
  const roundTo2 = (num) => {
    return Math.round(num * 100) / 100;
  };

  // Calculate surcharges, discounts, and sequential credit deductions for bills
  const calculateBillsWithSurcharge = (bills, customer) => {
    const sortedBills = [...bills].sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth));
    let remainingCredit = customer ? parseFloat(customer.credit_balance || 0) : 0;

    return sortedBills.map(bill => {
      const surchargeInfo = surchargeSettings
        ? paymentService.calculateSurcharge(bill, surchargeSettings)
        : null;
      
      const basicAmount = roundTo2(parseFloat(bill.basicamount || 0));
      
      let calculatedDiscountAmount = 0;
      if (bill.discountamount && parseFloat(bill.discountamount) > 0) {
        calculatedDiscountAmount = roundTo2(parseFloat(bill.discountamount));
      } else if (customer && customer.discount) {
        const customerDiscountPercent = parseFloat(customer.discount);
        calculatedDiscountAmount = roundTo2((basicAmount * customerDiscountPercent) / 100);
      }
      
      const basicAfterDiscount = roundTo2(Math.max(0, basicAmount - calculatedDiscountAmount));
      const surchargeAmount = roundTo2(surchargeInfo ? surchargeInfo.surchargeAmount : parseFloat(bill.surchargeamount || 0));
      
      let creditApplied = 0;
      if (remainingCredit > 0) {
        creditApplied = roundTo2(Math.min(remainingCredit, basicAfterDiscount));
        remainingCredit = roundTo2(remainingCredit - creditApplied);
      }

      const netBasic = roundTo2(basicAfterDiscount - creditApplied);
      const totalWithSurcharge = roundTo2(netBasic + surchargeAmount);
      
      return {
        ...bill,
        calculatedSurchargeAmount: surchargeAmount,
        calculatedDiscountAmount,
        creditApplied,
        calculatedTotal: totalWithSurcharge
      };
    });
  };

  useEffect(() => {
    const idFromQuery = searchParams.get('customerId');
    if (idFromQuery) {
      setCustomerId(idFromQuery);
      handleSearch(null, idFromQuery);
    }
  }, []);

  const handleSearch = async (e, overrideId) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const idToUse = (overrideId ?? customerId).trim();

    if (!idToUse) {
      setError('Please enter a Customer ID');
      return;
    }

    setSearchParams({ customerId: idToUse });

    setLoading(true);
    setError('');
    setSearched(true);
    setCustomer(null);
    setUnpaidBills([]);
    setPaymentHistory([]);

    try {
      // Fetch customer details
      const customerResponse = await customerService.getCustomerById(idToUse);
      
      if (!customerResponse || !customerResponse.success || !customerResponse.data) {
        setError('Customer ID not found. Please check and try again.');
        setLoading(false);
        return;
      }

      setCustomer(customerResponse.data);

      // Fetch unpaid bills
      const billsResponse = await billingService.getCustomerBills(idToUse);
      if (billsResponse.success) {
        const unpaid = billsResponse.data.filter(bill => bill.paymentstatus === 'Unpaid');
        // Calculate surcharges and discounts
        const calculatedUnpaid = calculateBillsWithSurcharge(unpaid, customerResponse.data);
        setUnpaidBills(calculatedUnpaid);
      }

      // Fetch payment history (paid bills)
      const paymentsResponse = await paymentService.getPaymentHistory(idToUse);
      if (paymentsResponse.success) {
        setPaymentHistory(paymentsResponse.data);
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('An error occurred while fetching data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const formatBillingMonth = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long'
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const selectedBills = unpaidBills.filter((bill) =>
    selectedBillIds.includes(bill.billid)
  );

  const selectedTotal = selectedBills.reduce(
    (sum, bill) => sum + (bill.calculatedTotal || 0),
    0
  );

  const billsForTotals = selectedBillIds.length > 0 ? selectedBills : unpaidBills;

  const totalOutstanding = billsForTotals.reduce(
    (sum, bill) => sum + (bill.calculatedTotal || 0),
    0
  );

  const totalDiscount = billsForTotals.reduce(
    (sum, bill) => sum + (bill.calculatedDiscountAmount || 0),
    0
  );

  const handleToggleBillSelection = (billId) => {
    setSelectedBillIds((prev) =>
      prev.includes(billId) ? prev.filter((id) => id !== billId) : [...prev, billId]
    );
  };

  const handleToggleSelectAll = () => {
    if (selectedBillIds.length === unpaidBills.length) {
      setSelectedBillIds([]);
    } else {
      setSelectedBillIds(unpaidBills.map((bill) => bill.billid));
    }
  };

  const handleStartOnlinePayment = async () => {
    if (!customer || selectedBillIds.length === 0) return;

    setOnlinePaymentLoading(true);
    setOnlinePaymentError('');

    try {
      const amountToPay = Math.max(
        0,
        selectedTotal - (customer.credit_balance || 0)
      );

      const result = await paymentService.createQrphPaymentSession({
        customerId: customer.customerid,
        billIds: selectedBillIds,
        amount: amountToPay,
        description: `Water bills payment for ${customer.customerid}`
      });

      if (!result.success) {
        setOnlinePaymentError(
          result.error || 'Failed to start online payment. Please try again.'
        );
        return;
      }
      
      const session = result.data;

      if (session && session.checkoutUrl) {
        if (session.checkoutSessionId) {
          const sessionInfo = {
            checkoutSessionId: session.checkoutSessionId,
            customerId: customer.customerid,
            billIds: selectedBillIds
          };
          localStorage.setItem('paymongoLastSession', JSON.stringify(sessionInfo));
        }
        window.location.href = session.checkoutUrl;
        return;
      }

      setOnlinePaymentSession(session);
      setOnlinePaymentDialogOpen(true);
    } catch (err) {
      setOnlinePaymentError(
        err.message || 'Failed to start online payment. Please try again.'
      );
    } finally {
      setOnlinePaymentLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8faf9', py: { xs: 3, md: 5 } }}>
      <Container maxWidth="md">
        {/* Search / Lookup Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3.5 },
            mb: 3.5,
            borderRadius: 4,
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.04)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{
                color: '#475569',
                fontWeight: 600,
                borderRadius: 2,
                px: 1.5,
                bgcolor: '#f1f5f9',
                '&:hover': { bgcolor: '#e2e8f0' }
              }}
            >
              Back to Home
            </Button>
            <Box 
              sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: 2.5, 
                bgcolor: 'rgba(5, 150, 105, 0.12)', 
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <WaterIcon sx={{ fontSize: 24 }} />
            </Box>
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography variant="h5" fontWeight={800} color="#0f172a">
              Water Billing & QRPh Payment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your Customer ID or Account Number to verify your active balance and settle payments securely online.
            </Typography>
          </Box>

          <form onSubmit={handleSearch}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                fullWidth
                placeholder="Enter Customer ID (e.g. CUST-001 or Account ID)"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2.5, bgcolor: '#ffffff' }
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                sx={{ 
                  px: 3.5, 
                  py: 1.25,
                  minWidth: { xs: '100%', sm: 140 },
                  bgcolor: '#059669',
                  color: '#ffffff',
                  fontWeight: 700,
                  borderRadius: 2.5,
                  boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
                  '&:hover': { bgcolor: '#047857' }
                }}
              >
                {loading ? 'Searching...' : 'Search Bill'}
              </Button>
            </Stack>
          </form>

          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2.5 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* Results Section */}
        {searched && !loading && customer && (
          <Box>
            {/* Customer Detail Card */}
            <Card
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 4,
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                p: { xs: 2, sm: 2.5 }
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Avatar sx={{ bgcolor: '#059669', color: '#ffffff', width: 48, height: 48, fontWeight: 700 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={800} color="#0f172a" noWrap>
                      {customer.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Account ID: <strong>{customer.customerid}</strong> • Barangay: <strong>{customer.barangay || 'N/A'}</strong>
                    </Typography>
                    {customer.type && (
                      <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>
                        Category: {customer.type} {customer.discountpercentage > 0 ? `(${customer.discountpercentage}% Discount)` : ''}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={customer.status || 'Active'}
                    size="small"
                    sx={{
                      bgcolor: customer.status === 'Active' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: customer.status === 'Active' ? '#059669' : '#ef4444',
                      fontWeight: 700,
                      fontSize: '0.75rem'
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Paper 
              elevation={0}
              sx={{ 
                borderRadius: 4, 
                border: '1px solid #e2e8f0', 
                bgcolor: '#ffffff',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', bgcolor: '#f8faf9', px: 2 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTabs-indicator': { bgcolor: '#059669', height: 3, borderRadius: '3px 3px 0 0' },
                    '& .MuiTab-root.Mui-selected': { color: '#059669', fontWeight: 700 }
                  }}
                >
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon sx={{ fontSize: 18, color: activeTab === 0 ? '#059669' : '#94a3b8' }} />
                        Unpaid Bills
                        {unpaidBills.length > 0 && (
                          <Chip 
                            label={unpaidBills.length} 
                            size="small" 
                            sx={{ bgcolor: '#ef4444', color: '#ffffff', height: 20, fontSize: '0.7rem', fontWeight: 700 }} 
                          />
                        )}
                      </Box>
                    } 
                  />
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon sx={{ fontSize: 18, color: activeTab === 1 ? '#059669' : '#94a3b8' }} />
                        Payment History
                      </Box>
                    } 
                  />
                </Tabs>
              </Box>

              <Box sx={{ p: { xs: 2, sm: 3 }, pb: isMobile ? 12 : 3 }}>
                {activeTab === 0 && (
                  <>
                    {unpaidBills.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 5 }}>
                        <CheckCircleIcon sx={{ color: '#059669', fontSize: 56, mb: 1.5 }} />
                        <Typography variant="h6" fontWeight={700} color="#0f172a">No Unpaid Bills</Typography>
                        <Typography variant="body2" color="text.secondary">All water consumption accounts are fully settled.</Typography>
                      </Box>
                    ) : (
                      <>
                        {isMobile ? (
                          <Stack spacing={1.5}>
                            {unpaidBills.map((bill) => (
                              <Card key={bill.billid} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 1.5 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 0.5
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox
                                      size="small"
                                      sx={{ color: '#059669', '&.Mui-checked': { color: '#059669' } }}
                                      checked={selectedBillIds.includes(bill.billid)}
                                      onChange={() => handleToggleBillSelection(bill.billid)}
                                    />
                                    <Typography variant="body2" fontWeight={700} color="#0f172a">
                                      {formatBillingMonth(bill.billedmonth)}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" fontWeight={800} color="#059669">
                                    {formatCurrency(bill.calculatedTotal)}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                                  {bill.previousreading} → {bill.currentreading} ({bill.consumption} m³)
                                </Typography>
                                <Box sx={{ pl: 4, mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Basic: {formatCurrency(bill.basicamount)}
                                  </Typography>
                                  {(bill.calculatedSurchargeAmount > 0 || bill.calculatedDiscountAmount > 0) && (
                                    <Typography variant="caption" sx={{ display: 'block', color: bill.calculatedSurchargeAmount > 0 ? '#ef4444' : '#059669' }}>
                                      {bill.calculatedSurchargeAmount > 0 && `+ Surcharge: ${formatCurrency(bill.calculatedSurchargeAmount)} `}
                                      {bill.calculatedDiscountAmount > 0 && `- Discount: ${formatCurrency(bill.calculatedDiscountAmount)}`}
                                    </Typography>
                                  )}
                                </Box>
                              </Card>
                            ))}
                          </Stack>
                        ) : (
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{ borderRadius: 3, border: '1px solid #e2e8f0', maxHeight: 420 }}
                          >
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      sx={{ color: '#059669', '&.Mui-checked': { color: '#059669' } }}
                                      checked={
                                        unpaidBills.length > 0 &&
                                        selectedBillIds.length === unpaidBills.length
                                      }
                                      indeterminate={
                                        selectedBillIds.length > 0 &&
                                        selectedBillIds.length < unpaidBills.length
                                      }
                                      onChange={handleToggleSelectAll}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Billing Period</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>Consumption</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>Basic Amount</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>Adj. (SC/ Disc)</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>Total Due</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {unpaidBills.map((bill) => (
                                  <TableRow key={bill.billid} hover>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        sx={{ color: '#059669', '&.Mui-checked': { color: '#059669' } }}
                                        checked={selectedBillIds.includes(bill.billid)}
                                        onChange={() => handleToggleBillSelection(bill.billid)}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight={600}>
                                        {formatBillingMonth(bill.billedmonth)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2">
                                        {bill.previousreading} - {bill.currentreading} ({bill.consumption} m³)
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2">
                                        {formatCurrency(bill.basicamount)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      {bill.calculatedSurchargeAmount > 0 && (
                                        <Typography variant="body2" color="#ef4444" fontWeight={600}>
                                          +{formatCurrency(bill.calculatedSurchargeAmount)}
                                        </Typography>
                                      )}
                                      {bill.calculatedDiscountAmount > 0 && (
                                        <Typography variant="body2" color="#059669" fontWeight={600}>
                                          -{formatCurrency(bill.calculatedDiscountAmount)}
                                        </Typography>
                                      )}
                                      {bill.calculatedSurchargeAmount <= 0 &&
                                        bill.calculatedDiscountAmount <= 0 && (
                                          <Typography variant="body2" color="text.secondary">
                                            —
                                          </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography
                                        variant="body2"
                                        fontWeight={800}
                                        color="#059669"
                                      >
                                        {formatCurrency(bill.calculatedTotal)}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}

                        {/* Summary & Pay Online Box */}
                        <Box
                          sx={{
                            mt: isMobile ? 0 : 3,
                            p: 2.5,
                            bgcolor: '#047857',
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                            color: '#ffffff',
                            borderRadius: 3.5,
                            position: isMobile ? 'fixed' : 'static',
                            left: isMobile ? 0 : 'auto',
                            right: isMobile ? 0 : 'auto',
                            bottom: isMobile ? 64 : 'auto',
                            width: isMobile ? '100%' : 'auto',
                            boxShadow: isMobile ? '0 -4px 20px rgba(0,0,0,0.15)' : '0 10px 20px -5px rgba(5, 150, 105, 0.3)',
                            zIndex: isMobile ? 1150 : 'auto'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {customer.credit_balance > 0 ? 'Net Outstanding Balance' : 'Total Outstanding Balance'}
                              </Typography>
                              {(customer.credit_balance > 0 || totalDiscount > 0) && (
                                <Typography variant="caption" sx={{ mt: 0.25, display: 'block', opacity: 0.9 }}>
                                  {customer.credit_balance > 0 && `Credits: ${formatCurrency(customer.credit_balance)}`}
                                  {customer.credit_balance > 0 && totalDiscount > 0 && ' · '}
                                  {totalDiscount > 0 && `Discounts: ${formatCurrency(totalDiscount)}`}
                                </Typography>
                              )}
                              {selectedBillIds.length > 0 && (
                                <Typography variant="caption" sx={{ mt: 0.25, display: 'block', opacity: 0.95, fontWeight: 600 }}>
                                  Selected ({selectedBillIds.length}): {formatCurrency(selectedTotal)}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="h5" fontWeight={800}>
                              {customer.credit_balance > 0
                                ? formatCurrency(Math.max(0, totalOutstanding - customer.credit_balance))
                                : formatCurrency(totalOutstanding)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              mt: 2,
                              display: 'flex',
                              flexDirection: isMobile ? 'column' : 'row',
                              gap: 1
                            }}
                          >
                            <Button
                              variant="contained"
                              fullWidth={isMobile}
                              disabled={selectedBillIds.length === 0 || onlinePaymentLoading}
                              onClick={handleStartOnlinePayment}
                              sx={{
                                bgcolor: '#ffffff',
                                color: '#047857',
                                fontWeight: 800,
                                borderRadius: 2.5,
                                py: 1,
                                px: 3,
                                '&:hover': { bgcolor: '#f1f5f9' },
                                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.4)', color: '#ffffff' }
                              }}
                            >
                              {onlinePaymentLoading
                                ? 'Starting QRPh Session...'
                                : selectedBillIds.length > 0
                                ? `Pay Online via QRPh (${selectedBillIds.length} Selected)`
                                : 'Select Bills to Pay Online'}
                            </Button>
                          </Box>
                          {onlinePaymentError && (
                            <Typography
                              variant="caption"
                              sx={{ mt: 0.75, display: 'block', color: '#fca5a5', fontWeight: 600 }}
                            >
                              {onlinePaymentError}
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </>
                )}

                {activeTab === 1 && (
                  <>
                    {paymentHistory.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 5 }}>
                        <HistoryIcon sx={{ color: '#94a3b8', fontSize: 56, mb: 1.5 }} />
                        <Typography variant="h6" fontWeight={700} color="#0f172a">No Payment History</Typography>
                        <Typography variant="body2" color="text.secondary">No historical paid transactions found for this account.</Typography>
                      </Box>
                    ) : isMobile ? (
                      <Stack spacing={1.5}>
                        {paymentHistory.slice(0, 10).map((bill) => (
                          <Card key={bill.billid} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'baseline',
                                mb: 0.5
                              }}
                            >
                              <Typography variant="body2" fontWeight={700} color="#0f172a">
                                {formatBillingMonth(bill.billedmonth)}
                              </Typography>
                              <Typography variant="body2" fontWeight={800} color="#059669">
                                {formatCurrency(bill.totalbillamount)}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              Consumption: {bill.consumption} m³ • Basic: {formatCurrency(bill.basicamount)}
                            </Typography>
                            <Chip 
                              icon={<CheckCircleIcon sx={{ fontSize: 14, color: '#059669 !important' }} />} 
                              label="Paid" 
                              size="small" 
                              sx={{ mt: 1, bgcolor: 'rgba(5, 150, 105, 0.1)', color: '#059669', fontWeight: 700 }} 
                            />
                          </Card>
                        ))}
                      </Stack>
                    ) : (
                      <Stack spacing={1.5}>
                        {paymentHistory.slice(0, 10).map((bill) => (
                          <Card key={bill.billid} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, bgcolor: '#f8faf9', p: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                  <CalendarIcon fontSize="small" sx={{ color: '#059669' }} /> Billing Period
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                                  {formatBillingMonth(bill.billedmonth)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                  <OpacityIcon fontSize="small" sx={{ color: '#059669' }} /> Consumption
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {bill.consumption} m³
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="caption" color="text.secondary">Amount Details</Typography>
                                <Typography variant="body2">
                                  Basic: {formatCurrency(bill.basicamount)} 
                                  {bill.surchargeamount > 0 && ` + Surcharge: ${formatCurrency(bill.surchargeamount)}`}
                                  {bill.discountamount > 0 && ` - Discount: ${formatCurrency(bill.discountamount)}`}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={2} sx={{ textAlign: 'right' }}>
                                <Typography variant="subtitle1" color="#059669" fontWeight={800}>
                                  {formatCurrency(bill.totalbillamount)}
                                </Typography>
                                <Chip 
                                  icon={<CheckCircleIcon sx={{ fontSize: '0.85rem !important', color: '#059669 !important' }} />} 
                                  label="Paid" 
                                  size="small" 
                                  sx={{ bgcolor: 'rgba(5, 150, 105, 0.1)', color: '#059669', fontWeight: 700 }}
                                />
                              </Grid>
                            </Grid>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </>
                )}
              </Box>
            </Paper>
          </Box>
        )}

        {/* No Results */}
        {searched && !loading && !customer && !error && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SearchIcon sx={{ fontSize: 56, color: '#94a3b8', mb: 2 }} />
            <Typography variant="h6" fontWeight={700} color="#0f172a">No Customer Found</Typography>
            <Typography variant="body2" color="text.secondary">Please check the Customer ID / Account Number and try again.</Typography>
          </Box>
        )}

        {/* PayMongo QRPh Modal */}
        <Dialog
          open={onlinePaymentDialogOpen}
          onClose={() => {
            setOnlinePaymentDialogOpen(false);
            setOnlinePaymentSession(null);
          }}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>Pay Online via QRPh</DialogTitle>
          <DialogContent dividers sx={{ borderColor: '#e2e8f0' }}>
            {onlinePaymentSession ? (
              <Typography variant="body2" color="text.secondary">
                You will be redirected to the secure PayMongo checkout portal to scan your QRPh code.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Payment session created. Please follow the checkout instructions to complete your payment.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => setOnlinePaymentDialogOpen(false)}
              sx={{ fontWeight: 700, color: '#64748b' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      {/* Sticky Mobile Bottom Navigation */}
      <MobileBottomBar
        items={[
          { name: 'Home', icon: <InfoIcon />, path: '/' },
          { name: 'Water Bills', icon: <WaterIcon />, path: '/check-water-bills' },
          { name: 'Unclaimed Checks', icon: <ReceiptIcon />, path: '/check-unclaimed-checks' },
          { name: 'Staff Portal', icon: <LoginIcon />, path: '/login' }
        ]}
        currentPath="/check-water-bills"
        onNavigate={(path) => navigate(path)}
      />
    </Box>
  );
};

export default CheckWaterBills;

